import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DiscussionDto, MessageDto } from '@workspace/data-types';
import { AuraChatBubbleComponent, AuraChatInputComponent, AuraCountdownComponent } from '@workspace/ng-aura';
import { UserStore } from '@workspace/ng-data-access';

export type DecoratedMessage = MessageDto & {
  dateHeader: string | null;
  isMe: boolean;
  isLastInGroup: boolean;
  isSameGroupAsPrevious: boolean;
  sending?: boolean;
  failed?: boolean;
  isReadByAll?: boolean;
  errorMessage?: string;
};

export interface ChatPendingAction {
  id?: string;
  type: string;
  title?: string;
  message: string;
  icon?: string;
  actionLabel?: string;
  severity?: 'primary' | 'warning' | 'info' | 'success';
  deadline?: Date | string;
  data?: unknown;
}

@Component({
  selector: 'core-discussion-chat',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    AuraChatBubbleComponent,
    AuraChatInputComponent,
    AuraCountdownComponent,
  ],
  host: {
    class: 'flex-1 flex flex-col min-w-0 min-h-0 h-full',
  },
  templateUrl: './discussion-chat.component.html',
  styles: [
    `
      @keyframes highlightFlash {
        0% {
          transform: scale(1.04) !important;
          filter: brightness(1.15) !important;
          outline: 3px solid var(--primary-9, #3b82f6) !important;
          outline-offset: 3px !important;
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.6) !important;
        }
        50% {
          transform: scale(1.04) !important;
          filter: brightness(1.15) !important;
          outline: 3px solid var(--primary-9, #3b82f6) !important;
          outline-offset: 3px !important;
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.6) !important;
        }
        100% {
          transform: scale(1) !important;
          filter: brightness(1) !important;
          outline: 3px solid transparent !important;
          outline-offset: 0 !important;
          box-shadow: none !important;
        }
      }

      ::ng-deep .highlight-flash {
        animation: highlightFlash 2.4s ease-in-out !important;
        transition: all 0.3s ease !important;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionChatCoreComponent {
  private readonly userStore = inject(UserStore);

  activeDiscussion = input<DiscussionDto | null>(null);
  decoratedMessages = input<DecoratedMessage[]>([]);
  isLoadingMessages = input<boolean>(false);
  isLoadingMore = input<boolean>(false);
  timeFormat = input<string>('HH:mm');
  showDeletePipe = input<(msg: MessageDto, idx: number, list: DecoratedMessage[]) => boolean>(() => false);
  subtitle = input<string>('En ligne');

  // Allow custom name/initial resolvers if business needs special mapping
  participantNameResolver = input<(discussion: DiscussionDto) => string>();
  participantInitialResolver = input<(discussion: DiscussionDto) => string>();
  participantProfileUrl = input<string | null>(null);
  participantAvatarUrl = input<string | null>(null);
  pinnedMessage = input<MessageDto | null>(null);
  clickableProfile = input<boolean>(false);
  pendingAction = input<ChatPendingAction | null>(null);
  pendingActions = input<ChatPendingAction[]>([]);

  profileClick = output<void>();
  pendingActionClick = output<ChatPendingAction>();

  readonly allPendingActions = computed<ChatPendingAction[]>(() => {
    const single = this.pendingAction();
    const multiple = this.pendingActions();
    if (single && !multiple.some((a) => a.type === single.type && (a.id ? a.id === single.id : true))) {
      return [single, ...multiple];
    }
    return multiple;
  });

  sendMessage = output<{ text: string; replyToId?: string }>();
  copyMessageText = output<MessageDto>();
  pinMessage = output<MessageDto>();
  infoMessage = output<MessageDto>();
  deleteMessage = output<MessageDto>();
  scrolledToTop = output<void>();
  readActiveDiscussion = output<void>();
  unpinMessage = output<void>();
  retryMessage = output<MessageDto>();
  actionMessageClick = output<MessageDto>();

  private lastScrollHeight = 0;
  private isPrependAction = false;

  @ViewChild('chatMessagesContainer') chatMessagesContainer!: ElementRef<HTMLDivElement>;

  readonly replyingTo = signal<MessageDto | null>(null);

  // We can initialize the effect here
  private readonly _scrollEffect = (() => {
    effect(() => {
      this.decoratedMessages();
      if (this.isPrependAction) {
        this.restoreScrollPosition();
        this.isPrependAction = false;
      }
    });
  })();

  readonly activeParticipantName = computed(() => {
    const active = this.activeDiscussion();
    if (!active) return '';
    const resolver = this.participantNameResolver();
    if (resolver) return resolver(active);
    return this.getParticipantName(active);
  });

  readonly activeParticipantInitial = computed(() => {
    const active = this.activeDiscussion();
    if (!active) return '';
    const resolver = this.participantInitialResolver();
    if (resolver) return resolver(active);
    return this.getParticipantInitial(active);
  });

  isMe(msg: MessageDto): boolean {
    const userId = this.userStore.current()?._id;
    if (!userId) return false;
    return msg.sender.toString() === userId.toString();
  }

  isActionMessage(msg: MessageDto): boolean {
    if (msg.type === 'action' || msg.type === 'system') return true;
    const text = msg.text || '';
    return (
      text.startsWith('📅 Bonjour, je vous propose 3') ||
      text.startsWith('🔄 Aucun des créneaux') ||
      text.startsWith('✅ Rendez-vous confirmé pour la visite') ||
      text.startsWith('Le propriétaire vous a proposé') ||
      text.startsWith('Le propriétaire a proposé')
    );
  }

  getActionIcon(msg: MessageDto): string {
    const actionType = msg.actionType;
    const text = msg.text || '';
    if (actionType === 'property_visit_confirmed' || text.startsWith('✅ Rendez-vous confirmé')) {
      return 'check_circle';
    }
    if (actionType === 'property_visit_rescheduled' || text.startsWith('🔄 Aucun des créneaux')) {
      return 'event_repeat';
    }
    if (
      actionType === 'property_visit_completed' ||
      text.includes('réalisée') ||
      text.includes('effectuée')
    ) {
      return 'task_alt';
    }
    return 'event';
  }

  getActionMessageText(msg: MessageDto): string {
    const isMe = this.isMe(msg);
    const actionType = msg.actionType;
    const text = msg.text || '';

    if (actionType === 'property_visit_proposed' || text.startsWith('📅 Bonjour, je vous propose 3')) {
      return isMe
        ? 'Vous avez proposé des créneaux de visite.'
        : 'Le propriétaire vient de vous proposer des créneaux de visite.';
    }

    if (actionType === 'property_visit_confirmed' || text.startsWith('✅ Rendez-vous confirmé')) {
      return isMe
        ? 'Vous avez confirmé le créneau de visite.'
        : "L'agent immobilier a confirmé le créneau de visite.";
    }

    if (actionType === 'property_visit_rescheduled' || text.startsWith('🔄 Aucun des créneaux')) {
      return isMe
        ? "Vous avez demandé d'autres disponibilités pour la visite."
        : "L'agent immobilier a demandé d'autres disponibilités pour la visite.";
    }

    if (
      actionType === 'property_visit_completed' ||
      text.includes('réalisée') ||
      text.includes('effectuée')
    ) {
      return 'La visite du bien a été marquée comme effectuée.';
    }

    return text;
  }

  getActionDeadline(msg: MessageDto): Date | null {
    if (msg.actionType !== 'property_visit_proposed' && msg.actionType !== 'property_visit_rescheduled') {
      return null;
    }
    const msgs = this.decoratedMessages();
    const isLatestVisitAction =
      [...msgs].reverse().find((m) => m.actionType?.startsWith('property_visit'))?._id === msg._id;
    if (!isLatestVisitAction) return null;

    if (msg.createdAt) {
      return new Date(new Date(msg.createdAt).getTime() + 24 * 60 * 60 * 1000);
    }
    return null;
  }

  getParticipantName(discussion: DiscussionDto): string {
    const userId = this.userStore.current()?._id;
    const other = discussion.participants.find(
      (p) => userId && p.participant.toString() !== userId.toString(),
    );
    if (other?.firstName) {
      return other.firstName;
    }
    return 'Utilisateur';
  }

  getParticipantInitial(discussion: DiscussionDto): string {
    const name = this.getParticipantName(discussion);
    return name.charAt(0).toUpperCase();
  }

  onSendFromInput(text: string) {
    const replyToId = this.replyingTo()?._id as unknown as string;
    this.sendMessage.emit({ text, replyToId });
    this.replyingTo.set(null);
  }

  getReplyToSenderName(msg: MessageDto): string | null {
    if (!msg.replyTo || typeof msg.replyTo === 'string') return null;
    const currentUserId = this.userStore.current()?._id;
    const sender = msg.replyTo.sender as unknown;

    if (sender && typeof sender === 'object') {
      const senderObj = sender as { _id?: string; id?: string; identity?: { firstName?: string } };
      const senderId = senderObj._id || senderObj.id;
      if (currentUserId && senderId && senderId.toString() === currentUserId.toString()) {
        return 'Vous';
      }
      return senderObj.identity?.firstName || 'Utilisateur';
    }

    if (sender && currentUserId && sender.toString() === currentUserId.toString()) {
      return 'Vous';
    }

    return this.activeParticipantName();
  }

  onScroll() {
    if (!this.chatMessagesContainer) return;
    const container = this.chatMessagesContainer.nativeElement;
    if (container.scrollTop <= 10 && !this.isLoadingMore() && !this.isLoadingMessages()) {
      this.lastScrollHeight = container.scrollHeight;
      this.isPrependAction = true;
      this.scrolledToTop.emit();
    }
  }

  private restoreScrollPosition() {
    setTimeout(() => {
      if (this.chatMessagesContainer) {
        const container = this.chatMessagesContainer.nativeElement;
        container.scrollTop = container.scrollHeight - this.lastScrollHeight;
      }
    }, 0);
  }

  scrollToBottom() {
    setTimeout(() => {
      if (this.chatMessagesContainer) {
        this.chatMessagesContainer.nativeElement.scrollTop =
          this.chatMessagesContainer.nativeElement.scrollHeight;
      }
    }, 100);
  }

  scrollToMessage(messageId: string | object | undefined | null) {
    if (!messageId) return;
    const idStr = messageId.toString();
    const element = document.getElementById(`message-${idStr}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const bubble = element.querySelector('.chat-bubble-content');
      if (bubble) {
        bubble.classList.add('highlight-flash');
        setTimeout(() => {
          bubble.classList.remove('highlight-flash');
        }, 4000);
      }
    }
  }

  scrollToActiveReply() {
    const reply = this.replyingTo();
    if (reply && reply._id) {
      this.scrollToMessage(reply._id as unknown as string);
    }
  }

  onInputFocusOrClick() {
    this.readActiveDiscussion.emit();
  }
}
